import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  FileSignature, Plus, Send, Clock, CheckCircle, XCircle, 
  AlertCircle, Eye, RefreshCw, Users, FileText, Loader2,
  Upload, File, Trash2, UserPlus, GripVertical, Download,
  Mail, PenLine, Calendar, Building, User, Archive, FolderOpen,
  FileSpreadsheet, Files, CheckCircle2, XOctagon, AlertTriangle
} from 'lucide-react';

const API_URL = window.location.origin;

// Helper function to format date without timezone shift
const formatDateSafe = (dateStr) => {
  if (!dateStr) return 'N/A';
  // Handle ISO date strings (YYYY-MM-DD) without timezone conversion
  const parts = dateStr.split('T')[0].split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return new Date(year, month - 1, day).toLocaleDateString();
  }
  return dateStr;
};

// Agreement templates
const AGREEMENT_TEMPLATES = [
  { 
    id: 'coaching_agreement', 
    name: 'Coaching Agreement',
    description: 'Standard coaching services agreement between coach and client',
    fields: ['client_name', 'coach_name', 'start_date', 'sessions', 'fee']
  },
  { 
    id: 'nda', 
    name: 'Non-Disclosure Agreement',
    description: 'Confidentiality agreement for sensitive discussions',
    fields: ['party_a', 'party_b', 'effective_date']
  },
  { 
    id: 'liability_waiver', 
    name: 'Liability Waiver',
    description: 'Release of liability for retreat or event participation',
    fields: ['participant_name', 'event_name', 'event_date']
  },
  { 
    id: 'retreat_agreement', 
    name: 'Retreat Agreement',
    description: 'Terms and conditions for retreat participation',
    fields: ['participant_name', 'retreat_name', 'dates', 'payment_terms']
  },
  { 
    id: 'custom', 
    name: 'Custom Document',
    description: 'Upload your own document for signature',
    fields: []
  }
];

const AdminESignatures = () => {
  const [agreements, setAgreements] = useState([]);
  const [archivedAgreements, setArchivedAgreements] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coachesLoading, setCoachesLoading] = useState(true);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUploadExistingDialog, setShowUploadExistingDialog] = useState(false);
  const [showBatchUploadDialog, setShowBatchUploadDialog] = useState(false);
  const [showEditArchivedDialog, setShowEditArchivedDialog] = useState(false);
  const [editingAgreement, setEditingAgreement] = useState(null);
  const [activeTab, setActiveTab] = useState('agreements');
  const [selectedAgreement, setSelectedAgreement] = useState(null);

  useEffect(() => {
    checkServiceStatus();
    fetchAgreements();
    fetchArchivedAgreements();
    fetchCoaches();
    fetchMembers();
  }, []);

  const checkServiceStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/esign/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServiceStatus(response.data);
    } catch (error) {
      console.error('Failed to check e-sign status:', error);
    }
  };

  const fetchAgreements = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/esign/agreements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out archived agreements
      setAgreements((response.data.agreements || []).filter(a => !a.is_archived));
    } catch (error) {
      console.error('Failed to fetch agreements:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedAgreements = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/esign/archived`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArchivedAgreements(response.data.agreements || []);
    } catch (error) {
      console.error('Failed to fetch archived agreements:', error);
    }
  };

  const handleDeleteArchivedAgreement = async (agreement) => {
    if (!window.confirm(`Are you sure you want to delete "${agreement.title}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/esign/archived/${agreement.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Archived agreement deleted successfully');
      fetchArchivedAgreements();
    } catch (error) {
      console.error('Failed to delete archived agreement:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete agreement');
    }
  };

  const fetchCoaches = async () => {
    setCoachesLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching coaches with token:', token ? 'exists' : 'missing');
      const response = await axios.get(`${API_URL}/api/coaches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Coaches response:', response.data);
      console.log('Coaches count:', Array.isArray(response.data) ? response.data.length : 'not array');
      setCoaches(response.data || []);
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
    } finally {
      setCoachesLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data?.filter(u => u.role !== 'admin') || []);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'draft': { color: 'bg-gray-100 text-gray-700', icon: FileText, label: 'Draft' },
      'sent': { color: 'bg-blue-100 text-blue-700', icon: Send, label: 'Sent' },
      'awaiting_signature': { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Awaiting Signature' },
      'partially_signed': { color: 'bg-purple-100 text-purple-700', icon: PenLine, label: 'Partially Signed' },
      'signed': { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Completed' },
      'complete': { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Completed' },
      'declined': { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Declined' },
      'expired': { color: 'bg-gray-100 text-gray-700', icon: AlertCircle, label: 'Expired' }
    };
    const config = statusConfig[status] || statusConfig['draft'];
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Service Status Banner */}
      {serviceStatus && !serviceStatus.configured && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">E-Signature Service Not Configured</p>
                <p className="text-sm text-amber-600">
                  Add DROPBOX_SIGN_API_KEY and DROPBOX_SIGN_CLIENT_ID to enable live e-signatures. 
                  Currently running in mock mode for testing.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold text-forest">E-Signature Management</h2>
          <p className="text-sm text-gray-500">Create, send, and track document signatures</p>
        </div>
        <Button 
          onClick={() => {
            if (coachesLoading) {
              toast.info('Loading coaches, please wait...');
              return;
            }
            setShowCreateDialog(true);
          }}
          className="bg-terracotta hover:bg-terracotta/90 text-white"
          disabled={coachesLoading}
        >
          {coachesLoading ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Loading...</>
          ) : (
            <><Plus className="h-4 w-4 mr-2" />New Agreement</>
          )}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-forest/10 flex items-center justify-center">
                <FileSignature className="h-5 w-5 text-forest" />
              </div>
              <div>
                <p className="text-2xl font-bold text-forest">{agreements.length}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-forest">
                  {agreements.filter(a => ['sent', 'awaiting_signature', 'partially_signed'].includes(a.status)).length}
                </p>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-forest">
                  {agreements.filter(a => ['signed', 'complete'].includes(a.status)).length}
                </p>
                <p className="text-xs text-gray-500">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-base">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Send className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-forest">
                  {agreements.filter(a => {
                    const created = new Date(a.created_at);
                    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                    return created > weekAgo;
                  }).length}
                </p>
                <p className="text-xs text-gray-500">This Week</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-sand/50">
          <TabsTrigger value="agreements">All Agreements</TabsTrigger>
          <TabsTrigger value="archived">Archived Agreements</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="agreements" className="mt-4">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-forest">Agreements</CardTitle>
              <CardDescription>Track signature status for all documents</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
                </div>
              ) : agreements.length === 0 ? (
                <div className="text-center py-12">
                  <FileSignature className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="font-heading text-lg text-forest mb-2">No Agreements Yet</h3>
                  <p className="text-gray-500 mb-4">Create your first e-signature request to get started</p>
                  <Button 
                    onClick={() => setShowCreateDialog(true)}
                    className="bg-terracotta hover:bg-terracotta/90 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Agreement
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {agreements.map((agreement) => (
                    <div 
                      key={agreement.id} 
                      className="flex items-center justify-between p-4 bg-sand/30 rounded-xl border border-sand hover:border-terracotta/30 transition-colors cursor-pointer"
                      onClick={() => setSelectedAgreement(agreement)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-lg bg-forest/10 flex items-center justify-center">
                          <FileSignature className="h-6 w-6 text-forest" />
                        </div>
                        <div>
                          <h4 className="font-medium text-forest">{agreement.title}</h4>
                          <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {agreement.signers?.length || 0} signers
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(agreement.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(agreement.status)}
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="archived" className="mt-4">
          <Card className="card-base">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                    <Archive className="h-5 w-5 text-terracotta" />
                    Archived Agreements
                  </CardTitle>
                  <CardDescription>Historical agreements uploaded for record keeping</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => setShowBatchUploadDialog(true)}
                    className="border-forest text-forest hover:bg-forest/5"
                    data-testid="batch-upload-btn"
                  >
                    <Files className="h-4 w-4 mr-2" />
                    Batch Upload
                  </Button>
                  <Button 
                    onClick={() => setShowUploadExistingDialog(true)}
                    className="bg-forest hover:bg-forest/90 text-white"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Single
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
                </div>
              ) : archivedAgreements.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="font-heading text-lg text-forest mb-2">No Archived Agreements</h3>
                  <p className="text-gray-500 mb-4">Upload existing signed agreements to keep them in one place</p>
                  <Button 
                    onClick={() => setShowUploadExistingDialog(true)}
                    className="bg-forest hover:bg-forest/90 text-white"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Existing Agreement
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {archivedAgreements.map((agreement) => (
                    <div 
                      key={agreement.id} 
                      className="flex items-center justify-between p-4 bg-sand/30 rounded-xl border border-sand hover:border-forest/30 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-lg bg-forest/10 flex items-center justify-center">
                          <Archive className="h-6 w-6 text-forest" />
                        </div>
                        <div>
                          <h4 className="font-medium text-forest">{agreement.title}</h4>
                          <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {agreement.parties?.map(p => p.name).filter(Boolean).join(', ') || 'No parties'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Signed: {formatDateSafe(agreement.signed_date)}
                            </span>
                          </div>
                          {agreement.notes && (
                            <p className="text-xs text-gray-400 mt-1 truncate max-w-md">{agreement.notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className="bg-blue-100 text-blue-700">
                          <Archive className="h-3 w-3 mr-1" />
                          Archived
                        </Badge>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setEditingAgreement(agreement);
                            setShowEditArchivedDialog(true);
                          }}
                          data-testid={`edit-archived-${agreement.id}`}
                        >
                          <PenLine className="h-4 w-4 text-forest" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => window.open(`${API_URL}/api/esign/agreements/${agreement.id}/download`, '_blank')}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteArchivedAgreement(agreement)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-archived-${agreement.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-forest">Agreement Templates</CardTitle>
              <CardDescription>Pre-configured templates for common agreements</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {AGREEMENT_TEMPLATES.map((template) => (
                  <div 
                    key={template.id}
                    className="p-4 border border-sand rounded-xl hover:border-terracotta/50 transition-colors cursor-pointer"
                    onClick={() => {
                      setShowCreateDialog(true);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-terracotta/10 flex items-center justify-center flex-shrink-0">
                        <FileText className="h-5 w-5 text-terracotta" />
                      </div>
                      <div>
                        <h4 className="font-medium text-forest">{template.name}</h4>
                        <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Agreement Dialog - only render when data is loaded */}
      {showCreateDialog && !coachesLoading && (
        <CreateAgreementDialog 
          key={`dialog-${coaches.length}-${members.length}`}
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          coaches={coaches}
          members={members}
          onSuccess={() => {
            fetchAgreements();
            setShowCreateDialog(false);
          }}
        />
      )}

      {/* Agreement Detail Dialog */}
      {selectedAgreement && (
        <AgreementDetailDialog
          agreement={selectedAgreement}
          open={!!selectedAgreement}
          onOpenChange={(open) => !open && setSelectedAgreement(null)}
          onRefresh={fetchAgreements}
        />
      )}

      {/* Upload Existing Agreement Dialog */}
      {showUploadExistingDialog && (
        <UploadExistingDialog 
          open={showUploadExistingDialog}
          onOpenChange={setShowUploadExistingDialog}
          coaches={coaches}
          members={members}
          onSuccess={() => {
            fetchArchivedAgreements();
            setShowUploadExistingDialog(false);
          }}
        />
      )}

      {/* Batch Upload Dialog */}
      {showBatchUploadDialog && (
        <BatchUploadDialog 
          open={showBatchUploadDialog}
          onOpenChange={setShowBatchUploadDialog}
          onSuccess={() => {
            fetchArchivedAgreements();
            setShowBatchUploadDialog(false);
          }}
        />
      )}

      {/* Edit Archived Agreement Dialog */}
      {showEditArchivedDialog && editingAgreement && (
        <EditArchivedAgreementDialog
          open={showEditArchivedDialog}
          onOpenChange={(open) => {
            setShowEditArchivedDialog(open);
            if (!open) setEditingAgreement(null);
          }}
          agreement={editingAgreement}
          onSuccess={() => {
            fetchArchivedAgreements();
            setShowEditArchivedDialog(false);
            setEditingAgreement(null);
          }}
        />
      )}
    </div>
  );
};

// Create Agreement Dialog Component
const CreateAgreementDialog = ({ open, onOpenChange, coaches, members, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);
  
  // Debug logging
  console.log('CreateAgreementDialog - coaches:', coaches?.length, 'members:', members?.length);
  const [formData, setFormData] = useState({
    title: '',
    template: '',
    message: '',
    document: null,
    documentName: '',
    signers: [
      { role: 'client', name: '', email: '', order: 1 }
    ]
  });

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('File size must be less than 10MB');
        return;
      }
      setFormData(prev => ({
        ...prev,
        document: file,
        documentName: file.name
      }));
    }
  };

  const addSigner = () => {
    setFormData(prev => ({
      ...prev,
      signers: [...prev.signers, { 
        role: 'signer', 
        name: '', 
        email: '', 
        order: prev.signers.length + 1 
      }]
    }));
  };

  const removeSigner = (index) => {
    if (formData.signers.length > 1) {
      setFormData(prev => ({
        ...prev,
        signers: prev.signers.filter((_, i) => i !== index)
      }));
    }
  };

  const updateSigner = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      signers: prev.signers.map((s, i) => 
        i === index ? { ...s, [field]: value } : s
      )
    }));
  };

  const selectMemberAsSigner = (index, memberId) => {
    const member = members.find(m => m.id === memberId);
    if (member) {
      updateSigner(index, 'name', member.full_name);
      updateSigner(index, 'email', member.email);
    }
  };

  const selectCoachAsSigner = (index, coachId) => {
    const coach = coaches.find(c => c.id === coachId);
    if (coach) {
      // Coaches may have 'name' or 'full_name' but typically don't have email
      updateSigner(index, 'name', coach.full_name || coach.name);
      // Email needs to be entered manually for coaches
      toast.info(`Please enter the email for ${coach.full_name || coach.name}`);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter an agreement title');
      return;
    }
    if (!formData.document && formData.template === 'custom') {
      toast.error('Please upload a document');
      return;
    }
    if (formData.signers.some(s => !s.name.trim() || !s.email.trim())) {
      toast.error('Please fill in all signer details');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('template', formData.template || 'custom');
      submitData.append('message', formData.message);
      submitData.append('signers', JSON.stringify(formData.signers));
      if (formData.document) {
        submitData.append('document', formData.document);
      }

      await axios.post(
        `${API_URL}/api/esign/create-agreement`,
        submitData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      toast.success('Agreement created and sent for signature!');
      onSuccess();
      
      // Reset form
      setFormData({
        title: '',
        template: '',
        message: '',
        document: null,
        documentName: '',
        signers: [{ role: 'client', name: '', email: '', order: 1 }]
      });
      setStep(1);
    } catch (error) {
      console.error('Failed to create agreement:', error);
      toast.error(error.response?.data?.detail || 'Failed to create agreement');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-terracotta" />
            Create E-Signature Agreement
          </DialogTitle>
          <DialogDescription>
            Step {step} of 3: {step === 1 ? 'Document Details' : step === 2 ? 'Add Signers' : 'Review & Send'}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-terracotta text-white' : 'bg-sand text-gray-500'
              }`}>
                {s}
              </div>
              {s < 3 && (
                <div className={`w-16 md:w-24 h-1 mx-2 rounded ${
                  step > s ? 'bg-terracotta' : 'bg-sand'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Document Details */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label>Agreement Title *</Label>
              <Input 
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Coaching Agreement - Jane Doe"
              />
            </div>

            <div>
              <Label>Template (Optional)</Label>
              <Select 
                value={formData.template}
                onValueChange={(val) => setFormData(prev => ({ ...prev, template: val }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a template or upload custom" />
                </SelectTrigger>
                <SelectContent>
                  {AGREEMENT_TEMPLATES.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Upload Document *</Label>
              <div className="mt-2">
                {formData.documentName ? (
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <File className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-green-700 flex-1">{formData.documentName}</span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, document: null, documentName: '' }))}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-sand rounded-xl cursor-pointer hover:border-terracotta/50 transition-colors">
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">Click to upload PDF, DOCX, or DOC</span>
                    <span className="text-xs text-gray-400 mt-1">Max 10MB</span>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileUpload}
                    />
                  </label>
                )}
              </div>
            </div>

            <div>
              <Label>Message to Signers (Optional)</Label>
              <Textarea 
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Add a personal message that will be included in the signing invitation email"
                rows={3}
              />
            </div>
          </div>
        )}

        {/* Step 2: Signers */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Signers (in signing order)</Label>
              <Button variant="outline" size="sm" onClick={addSigner}>
                <UserPlus className="h-4 w-4 mr-1" />
                Add Signer
              </Button>
            </div>

            <div className="space-y-4">
              {formData.signers.map((signer, index) => (
                <div key={index} className="p-4 border border-sand rounded-xl bg-sand/20">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-6 w-6 rounded-full bg-terracotta text-white text-xs flex items-center justify-center">
                      {index + 1}
                    </div>
                    <Select 
                      value={signer.role}
                      onValueChange={(val) => updateSigner(index, 'role', val)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="client">Client</SelectItem>
                        <SelectItem value="coach">Coach</SelectItem>
                        <SelectItem value="witness">Witness</SelectItem>
                        <SelectItem value="signer">Other Signer</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.signers.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="ml-auto"
                        onClick={() => removeSigner(index)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>

                  {/* Quick select from existing users */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div>
                      <select 
                        className="w-full p-2 text-xs border border-sand rounded-lg bg-white focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta outline-none"
                        onChange={(e) => e.target.value && selectMemberAsSigner(index, e.target.value)}
                        defaultValue=""
                      >
                        <option value="">Select member...</option>
                        {members && members.map((m) => (
                          <option key={m.id} value={m.id}>{m.full_name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <select 
                        className="w-full p-2 text-xs border border-sand rounded-lg bg-white focus:ring-2 focus:ring-terracotta/20 focus:border-terracotta outline-none"
                        onChange={(e) => e.target.value && selectCoachAsSigner(index, e.target.value)}
                        defaultValue=""
                      >
                        <option value="">Select coach...</option>
                        {coaches && coaches.map((c) => (
                          <option key={c.id} value={c.id}>{c.full_name || c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Full Name *</Label>
                      <Input 
                        value={signer.name}
                        onChange={(e) => updateSigner(index, 'name', e.target.value)}
                        placeholder="Signer's full name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Email *</Label>
                      <Input 
                        type="email"
                        value={signer.email}
                        onChange={(e) => updateSigner(index, 'email', e.target.value)}
                        placeholder="signer@email.com"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-xs text-gray-500">
              Signers will receive the document in the order listed above. Each signer must complete their signature before the next signer is notified.
            </p>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <div className="space-y-4">
            <Card className="bg-sand/30">
              <CardContent className="p-4">
                <h4 className="font-medium text-forest mb-3">Agreement Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Title:</span>
                    <span className="font-medium">{formData.title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Document:</span>
                    <span className="font-medium">{formData.documentName || 'Using template'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Signers:</span>
                    <span className="font-medium">{formData.signers.length}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div>
              <Label className="mb-2 block">Signing Order</Label>
              <div className="space-y-2">
                {formData.signers.map((signer, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-white border border-sand rounded-lg">
                    <div className="h-6 w-6 rounded-full bg-terracotta text-white text-xs flex items-center justify-center">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{signer.name}</p>
                      <p className="text-xs text-gray-500">{signer.email}</p>
                    </div>
                    <Badge variant="outline" className="capitalize">{signer.role}</Badge>
                  </div>
                ))}
              </div>
            </div>

            {formData.message && (
              <div>
                <Label className="mb-2 block">Message to Signers</Label>
                <p className="text-sm text-gray-600 bg-sand/30 p-3 rounded-lg">{formData.message}</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-6">
          <div className="flex gap-3 w-full">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(s => s - 1)} className="flex-1">
                Back
              </Button>
            )}
            {step < 3 ? (
              <Button 
                onClick={() => setStep(s => s + 1)} 
                className="flex-1 bg-terracotta hover:bg-terracotta/90 text-white"
                disabled={step === 1 && (!formData.title || (!formData.document && formData.template === 'custom'))}
              >
                Continue
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={creating}
                className="flex-1 bg-terracotta hover:bg-terracotta/90 text-white"
              >
                {creating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" />Send for Signature</>
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Agreement Detail Dialog
const AgreementDetailDialog = ({ agreement, open, onOpenChange, onRefresh }) => {
  const [sendingReminder, setSendingReminder] = useState(false);

  const sendReminder = async (signerEmail) => {
    setSendingReminder(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/esign/agreements/${agreement.id}/remind`,
        { email: signerEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reminder sent!');
    } catch (error) {
      toast.error('Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-forest">
            {agreement.title}
          </DialogTitle>
          <DialogDescription>
            Created {new Date(agreement.created_at).toLocaleDateString()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Status</span>
            <Badge className={
              ['signed', 'complete'].includes(agreement.status) 
                ? 'bg-green-100 text-green-700'
                : 'bg-amber-100 text-amber-700'
            }>
              {agreement.status}
            </Badge>
          </div>

          <div>
            <Label className="mb-2 block">Signers</Label>
            <div className="space-y-2">
              {agreement.signers?.map((signer, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-sand/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-forest/10 flex items-center justify-center">
                      <User className="h-4 w-4 text-forest" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{signer.name}</p>
                      <p className="text-xs text-gray-500">{signer.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {signer.signed_at ? (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Signed
                      </Badge>
                    ) : (
                      <>
                        <Badge className="bg-amber-100 text-amber-700">
                          <Clock className="h-3 w-3 mr-1" />
                          Pending
                        </Badge>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => sendReminder(signer.email)}
                          disabled={sendingReminder}
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {['signed', 'complete'].includes(agreement.status) && (
            <Button variant="outline" className="w-full">
              <Download className="h-4 w-4 mr-2" />
              Download Signed Document
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Upload Existing Agreement Dialog
const UploadExistingDialog = ({ open, onOpenChange, coaches, members, onSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    agreement_type: 'coaching',
    signed_date: '',
    coach_id: '',
    coach_name: '',
    coach_email: '',
    client_id: '',
    client_name: '',
    client_email: '',
    notes: '',
    document: null,
    documentName: ''
  });

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 20 * 1024 * 1024) { // 20MB limit
        toast.error('File size must be less than 20MB');
        return;
      }
      setFormData(prev => ({
        ...prev,
        document: file,
        documentName: file.name
      }));
    }
  };

  const selectCoach = (coachId) => {
    const coach = coaches.find(c => c.id === coachId);
    if (coach) {
      setFormData(prev => ({
        ...prev,
        coach_id: coachId,
        coach_name: coach.full_name || coach.name || ''
      }));
    }
  };

  const selectClient = (clientId) => {
    const client = members.find(m => m.id === clientId);
    if (client) {
      setFormData(prev => ({
        ...prev,
        client_id: clientId,
        client_name: client.full_name || '',
        client_email: client.email || ''
      }));
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter an agreement title');
      return;
    }
    if (!formData.document) {
      toast.error('Please upload a document');
      return;
    }
    if (!formData.signed_date) {
      toast.error('Please select the signed date');
      return;
    }
    if (!formData.coach_id && !formData.client_id && !formData.coach_email && !formData.client_email) {
      toast.error('Please specify at least one coach or client');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('agreement_type', formData.agreement_type);
      submitData.append('signed_date', formData.signed_date);
      submitData.append('document', formData.document);
      
      if (formData.coach_id) submitData.append('coach_id', formData.coach_id);
      if (formData.coach_name) submitData.append('coach_name', formData.coach_name);
      if (formData.coach_email) submitData.append('coach_email', formData.coach_email);
      if (formData.client_id) submitData.append('client_id', formData.client_id);
      if (formData.client_name) submitData.append('client_name', formData.client_name);
      if (formData.client_email) submitData.append('client_email', formData.client_email);
      if (formData.notes) submitData.append('notes', formData.notes);

      await axios.post(
        `${API_URL}/api/esign/upload-existing`,
        submitData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      toast.success('Agreement uploaded successfully!');
      onSuccess();
      
      // Reset form
      setFormData({
        title: '',
        agreement_type: 'coaching',
        signed_date: '',
        coach_id: '',
        coach_name: '',
        coach_email: '',
        client_id: '',
        client_name: '',
        client_email: '',
        notes: '',
        document: null,
        documentName: ''
      });
    } catch (error) {
      console.error('Failed to upload agreement:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload agreement');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
            <Archive className="h-5 w-5 text-forest" />
            Upload Existing Agreement
          </DialogTitle>
          <DialogDescription>
            Upload a signed agreement that was created outside this app for record keeping
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Title */}
          <div>
            <Label>Agreement Title *</Label>
            <Input 
              data-testid="upload-agreement-title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Coaching Agreement - Jane Doe (2023)"
            />
          </div>

          {/* Agreement Type */}
          <div>
            <Label>Agreement Type *</Label>
            <Select 
              value={formData.agreement_type}
              onValueChange={(val) => setFormData(prev => ({ ...prev, agreement_type: val }))}
            >
              <SelectTrigger data-testid="upload-agreement-type">
                <SelectValue placeholder="Select agreement type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="coaching">Coaching Agreement</SelectItem>
                <SelectItem value="nda">Non-Disclosure Agreement</SelectItem>
                <SelectItem value="liability">Liability Waiver</SelectItem>
                <SelectItem value="retreat">Retreat Agreement</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Signed Date */}
          <div>
            <Label>Date Signed *</Label>
            <Input 
              data-testid="upload-signed-date"
              type="date"
              value={formData.signed_date}
              onChange={(e) => setFormData(prev => ({ ...prev, signed_date: e.target.value }))}
            />
          </div>

          {/* Document Upload */}
          <div>
            <Label>Upload Document *</Label>
            <div className="mt-2">
              {formData.documentName ? (
                <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <File className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-green-700 flex-1">{formData.documentName}</span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setFormData(prev => ({ ...prev, document: null, documentName: '' }))}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-sand rounded-xl cursor-pointer hover:border-forest/50 transition-colors">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-500">Click to upload PDF, DOCX, or image</span>
                  <span className="text-xs text-gray-400 mt-1">Max 20MB</span>
                  <input 
                    data-testid="upload-agreement-file"
                    type="file" 
                    className="hidden" 
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                  />
                </label>
              )}
            </div>
          </div>

          {/* Coach Selection */}
          <div className="p-4 border border-sand rounded-xl bg-sand/20">
            <h4 className="font-medium text-forest text-sm mb-3 flex items-center gap-2">
              <Building className="h-4 w-4" />
              Coach (Party 1)
            </h4>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Select from coaches</Label>
                <select 
                  data-testid="upload-coach-select"
                  className="w-full p-2 text-sm border border-sand rounded-lg bg-white focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
                  value={formData.coach_id}
                  onChange={(e) => selectCoach(e.target.value)}
                >
                  <option value="">Select a coach...</option>
                  {coaches.map((c) => (
                    <option key={c.id} value={c.id}>{c.full_name || c.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input 
                    data-testid="upload-coach-name"
                    value={formData.coach_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, coach_name: e.target.value }))}
                    placeholder="Coach name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input 
                    data-testid="upload-coach-email"
                    type="email"
                    value={formData.coach_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, coach_email: e.target.value }))}
                    placeholder="coach@email.com"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Client Selection */}
          <div className="p-4 border border-sand rounded-xl bg-sand/20">
            <h4 className="font-medium text-forest text-sm mb-3 flex items-center gap-2">
              <User className="h-4 w-4" />
              Client (Party 2)
            </h4>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Select from members</Label>
                <select 
                  data-testid="upload-client-select"
                  className="w-full p-2 text-sm border border-sand rounded-lg bg-white focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
                  value={formData.client_id}
                  onChange={(e) => selectClient(e.target.value)}
                >
                  <option value="">Select a member...</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>{m.full_name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input 
                    data-testid="upload-client-name"
                    value={formData.client_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                    placeholder="Client name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input 
                    data-testid="upload-client-email"
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                    placeholder="client@email.com"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label>Notes (Optional)</Label>
            <Textarea 
              data-testid="upload-agreement-notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any additional notes about this agreement..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button 
            data-testid="upload-agreement-submit"
            onClick={handleSubmit}
            disabled={uploading}
            className="bg-forest hover:bg-forest/90 text-white"
          >
            {uploading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" />Upload Agreement</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Batch Upload Dialog Component
const BatchUploadDialog = ({ open, onOpenChange, onSuccess }) => {
  const [uploadMode, setUploadMode] = useState('files'); // 'files' or 'csv'
  const [step, setStep] = useState(1); // 1: Upload, 2: Edit Details, 3: Results
  const [csvFile, setCsvFile] = useState(null);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [fileDetails, setFileDetails] = useState([]); // For direct file upload mode
  const [parsedData, setParsedData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [coaches, setCoaches] = useState([]);

  // Fetch coaches for dropdown
  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/coaches`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCoaches(response.data || []);
      } catch (error) {
        console.error('Failed to fetch coaches:', error);
      }
    };
    if (open) fetchCoaches();
  }, [open]);

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/esign/batch-upload-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'batch_upload_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please upload a CSV file');
        return;
      }
      setCsvFile(file);
      
      // Parse CSV to preview
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const text = event.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          
          if (lines.length < 2) {
            toast.error('CSV file must have headers and at least one data row');
            setCsvFile(null);
            return;
          }
          
          // Parse headers
          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
          
          // Required headers check
          const requiredHeaders = ['title', 'agreement_type', 'signed_date', 'document_filename'];
          const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
          if (missingHeaders.length > 0) {
            toast.error(`Missing required columns: ${missingHeaders.join(', ')}`);
            setCsvFile(null);
            return;
          }
          
          const data = [];
          for (let i = 1; i < lines.length; i++) {
            // Handle quoted values with commas
            const values = [];
            let current = '';
            let inQuotes = false;
            
            for (let char of lines[i]) {
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                values.push(current.trim().replace(/"/g, ''));
                current = '';
              } else {
                current += char;
              }
            }
            values.push(current.trim().replace(/"/g, ''));
            
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            
            // Only add rows with at least a title
            if (row.title) {
              data.push(row);
            }
          }
          
          if (data.length === 0) {
            toast.error('No valid data rows found in CSV');
            setCsvFile(null);
            return;
          }
          
          setParsedData(data);
          toast.success(`Parsed ${data.length} row(s) from CSV`);
        } catch (error) {
          console.error('CSV parsing error:', error);
          toast.error('Failed to parse CSV file');
          setCsvFile(null);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read CSV file');
        setCsvFile(null);
      };
      reader.readAsText(file);
    }
  };

  const handleDocumentsUpload = (e) => {
    const files = Array.from(e.target.files);
    setDocumentFiles(prev => [...prev, ...files]);
    
    // For direct file mode, create default details for each new file
    if (uploadMode === 'files') {
      const newDetails = files.map(file => ({
        file: file,
        title: file.name.replace(/\.[^/.]+$/, '').replace(/_/g, ' ').replace(/-/g, ' '),
        agreement_type: 'coaching',
        signed_date: new Date().toISOString().split('T')[0],
        coach_id: '',
        coach_name: '',
        coach_email: '',
        client_name: '',
        client_email: '',
        notes: ''
      }));
      setFileDetails(prev => [...prev, ...newDetails]);
    }
  };

  const removeDocument = (index) => {
    setDocumentFiles(prev => prev.filter((_, i) => i !== index));
    if (uploadMode === 'files') {
      setFileDetails(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateFileDetail = (index, field, value) => {
    setFileDetails(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // If coach_id is selected, auto-fill coach name and email
      if (field === 'coach_id' && value) {
        const coach = coaches.find(c => c.id === value);
        if (coach) {
          updated[index].coach_name = coach.name;
          updated[index].coach_email = coach.email || '';
        }
      }
      
      return updated;
    });
  };

  const getMatchStatus = (filename) => {
    return documentFiles.some(f => f.name === filename);
  };

  const handleSubmit = async () => {
    // Validate
    const missingDocs = parsedData.filter(row => !getMatchStatus(row.document_filename));
    if (missingDocs.length > 0) {
      toast.error(`Missing ${missingDocs.length} document file(s). Please upload all referenced files.`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('csv_file', csvFile);
      documentFiles.forEach(file => {
        formData.append('documents', file);
      });

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      const response = await axios.post(
        `${API_URL}/api/esign/batch-upload-archived`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);
      setResults(response.data.results);
      setStep(3);

      if (response.data.results.success.length > 0) {
        toast.success(`${response.data.results.success.length} agreements uploaded successfully!`);
      }
      if (response.data.results.failed.length > 0) {
        toast.warning(`${response.data.results.failed.length} agreements failed to upload.`);
      }
    } catch (error) {
      console.error('Batch upload failed:', error);
      toast.error(error.response?.data?.detail || 'Batch upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (results && results.success.length > 0) {
      onSuccess();
    }
    // Reset state
    setUploadMode('files');
    setCsvFile(null);
    setDocumentFiles([]);
    setFileDetails([]);
    setParsedData([]);
    setResults(null);
    setStep(1);
    onOpenChange(false);
  };

  // Handle submit for direct file upload mode
  const handleDirectUpload = async () => {
    if (fileDetails.length === 0) {
      toast.error('Please upload at least one document');
      return;
    }

    // Validate all files have at least a title
    const missingTitles = fileDetails.filter(d => !d.title.trim());
    if (missingTitles.length > 0) {
      toast.error('Please provide a title for all documents');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const results = { success: [], failed: [] };
    const totalFiles = fileDetails.length;

    try {
      const token = localStorage.getItem('token');

      for (let i = 0; i < fileDetails.length; i++) {
        const detail = fileDetails[i];
        setUploadProgress(Math.round((i / totalFiles) * 100));

        try {
          const formData = new FormData();
          formData.append('file', detail.file);
          formData.append('title', detail.title);
          formData.append('agreement_type', detail.agreement_type);
          formData.append('signed_date', detail.signed_date);
          formData.append('coach_id', detail.coach_id || '');
          formData.append('coach_name', detail.coach_name || '');
          formData.append('coach_email', detail.coach_email || '');
          formData.append('client_name', detail.client_name || '');
          formData.append('client_email', detail.client_email || '');
          formData.append('notes', detail.notes || '');

          const response = await axios.post(
            `${API_URL}/api/esign/upload-archived`,
            formData,
            { 
              headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          results.success.push({
            title: detail.title,
            agreement_id: response.data.agreement?.id
          });
        } catch (error) {
          results.failed.push({
            title: detail.title,
            error: error.response?.data?.detail || 'Upload failed'
          });
        }
      }

      setUploadProgress(100);
      setResults(results);
      setStep(3);

      if (results.success.length > 0) {
        toast.success(`${results.success.length} agreement(s) uploaded successfully!`);
      }
      if (results.failed.length > 0) {
        toast.warning(`${results.failed.length} agreement(s) failed to upload.`);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
            <Files className="h-5 w-5 text-forest" />
            Batch Upload Agreements
          </DialogTitle>
          <DialogDescription>
            {step === 1 && "Upload a CSV file and matching document files to import multiple agreements at once."}
            {step === 2 && "Review the data before uploading."}
            {step === 3 && "Upload complete. Review the results below."}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-forest text-white' : 'bg-sand text-gray-500'
              }`}>
                {step > s ? <CheckCircle2 className="h-5 w-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-12 md:w-20 h-1 mx-2 rounded ${
                  step > s ? 'bg-forest' : 'bg-sand'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Upload Files */}
        {step === 1 && (
          <div className="space-y-4">
            {/* Mode Selection */}
            <div className="flex gap-2 p-1 bg-sand/50 rounded-lg">
              <button
                onClick={() => {
                  setUploadMode('files');
                  setCsvFile(null);
                  setParsedData([]);
                }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  uploadMode === 'files' 
                    ? 'bg-white text-forest shadow-sm' 
                    : 'text-gray-600 hover:text-forest'
                }`}
              >
                <Upload className="h-4 w-4 inline mr-2" />
                Upload Files
              </button>
              <button
                onClick={() => {
                  setUploadMode('csv');
                  setDocumentFiles([]);
                  setFileDetails([]);
                }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  uploadMode === 'csv' 
                    ? 'bg-white text-forest shadow-sm' 
                    : 'text-gray-600 hover:text-forest'
                }`}
              >
                <FileSpreadsheet className="h-4 w-4 inline mr-2" />
                Use CSV Template
              </button>
            </div>

            {/* Direct File Upload Mode */}
            {uploadMode === 'files' && (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-sm text-blue-700">
                    <strong>Quick Upload:</strong> Select multiple PDF files to upload. You can edit the details for each file in the next step.
                  </p>
                </div>

                {/* Document Files Upload */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Files className="h-4 w-4" />
                    Select Documents
                  </Label>
                  <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-sand rounded-xl cursor-pointer hover:border-forest/50 transition-colors">
                    <Files className="h-8 w-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">Click to select PDF files</span>
                    <span className="text-xs text-gray-400 mt-1">You can select multiple files at once</span>
                    <input 
                      data-testid="batch-files-upload"
                      type="file" 
                      className="hidden" 
                      accept=".pdf,.doc,.docx"
                      multiple
                      onChange={handleDocumentsUpload}
                    />
                  </label>
                </div>

                {/* Uploaded Files List */}
                {documentFiles.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-600">{documentFiles.length} file(s) selected</Label>
                    <div className="max-h-48 overflow-auto space-y-2">
                      {documentFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-sand/30 rounded-lg">
                          <File className="h-4 w-4 text-forest flex-shrink-0" />
                          <span className="text-sm flex-1 truncate">{file.name}</span>
                          <span className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeDocument(idx)}
                            className="h-6 w-6 p-0"
                          >
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CSV Mode */}
            {uploadMode === 'csv' && (
              <div className="space-y-4">
                {/* Template Download */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-blue-800">Need a template?</h4>
                      <p className="text-sm text-blue-600">Download our CSV template with example data</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={downloadTemplate}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      data-testid="download-template-btn"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>

                {/* CSV Upload */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <FileSpreadsheet className="h-4 w-4" />
                    CSV File *
                  </Label>
                  {csvFile ? (
                    <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <FileSpreadsheet className="h-5 w-5 text-green-600" />
                      <span className="text-sm text-green-700 flex-1">{csvFile.name}</span>
                      <Badge className="bg-green-100 text-green-700">{parsedData.length} rows</Badge>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setCsvFile(null);
                          setParsedData([]);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-sand rounded-xl cursor-pointer hover:border-forest/50 transition-colors">
                      <FileSpreadsheet className="h-8 w-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">Click to upload CSV file</span>
                      <input 
                        data-testid="batch-csv-upload"
                        type="file" 
                        className="hidden" 
                        accept=".csv"
                        onChange={handleCsvUpload}
                      />
                    </label>
                  )}
                </div>

                {/* Document Files Upload for CSV mode */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Files className="h-4 w-4" />
                    Document Files *
                  </Label>
                  <p className="text-xs text-amber-600 mb-2 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Important: File names must match exactly with the "document_filename" column in your CSV
                  </p>
                  <div className="space-y-2">
                    {documentFiles.length > 0 && (
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        {documentFiles.map((file, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-sand/30 rounded">
                            <File className="h-4 w-4 text-forest" />
                            <span className="text-xs truncate flex-1">{file.name}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeDocument(idx)}
                              className="h-6 w-6 p-0"
                            >
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <label className="flex items-center justify-center p-4 border-2 border-dashed border-sand rounded-xl cursor-pointer hover:border-forest/50 transition-colors">
                      <Files className="h-6 w-6 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-500">
                        {documentFiles.length > 0 ? 'Add more files' : 'Click to upload document files'}
                      </span>
                      <input 
                        data-testid="batch-docs-upload"
                        type="file" 
                        className="hidden" 
                        accept=".pdf,.doc,.docx"
                        multiple
                        onChange={handleDocumentsUpload}
                      />
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Edit Details / Preview */}
        {step === 2 && (
          <div className="space-y-4">
            {/* Direct file mode - Edit Details */}
            {uploadMode === 'files' && (
              <>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    Edit the details for each document below. Required fields are marked with *.
                  </p>
                </div>
                
                <div className="max-h-[400px] overflow-auto space-y-4">
                  {fileDetails.map((detail, idx) => (
                    <div key={idx} className="p-4 border border-sand rounded-lg bg-sand/10">
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-sand">
                        <File className="h-4 w-4 text-forest" />
                        <span className="font-medium text-sm truncate flex-1">{detail.file.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDocument(idx)}
                          className="h-6 w-6 p-0"
                        >
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Title *</Label>
                          <Input
                            value={detail.title}
                            onChange={(e) => updateFileDetail(idx, 'title', e.target.value)}
                            placeholder="Agreement title"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Agreement Type *</Label>
                          <Select
                            value={detail.agreement_type}
                            onValueChange={(v) => updateFileDetail(idx, 'agreement_type', v)}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="coaching">Coaching Agreement</SelectItem>
                              <SelectItem value="nda">NDA</SelectItem>
                              <SelectItem value="service">Service Agreement</SelectItem>
                              <SelectItem value="membership">Membership Agreement</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Signed Date *</Label>
                          <Input
                            type="date"
                            value={detail.signed_date}
                            onChange={(e) => updateFileDetail(idx, 'signed_date', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Coach</Label>
                          <Select
                            value={detail.coach_id}
                            onValueChange={(v) => updateFileDetail(idx, 'coach_id', v)}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="Select coach (optional)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="">No coach</SelectItem>
                              {coaches.map(coach => (
                                <SelectItem key={coach.id} value={coach.id}>
                                  {coach.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Client Name</Label>
                          <Input
                            value={detail.client_name}
                            onChange={(e) => updateFileDetail(idx, 'client_name', e.target.value)}
                            placeholder="Client name (optional)"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Client Email</Label>
                          <Input
                            type="email"
                            value={detail.client_email}
                            onChange={(e) => updateFileDetail(idx, 'client_email', e.target.value)}
                            placeholder="client@email.com (optional)"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">Notes</Label>
                          <Input
                            value={detail.notes}
                            onChange={(e) => updateFileDetail(idx, 'notes', e.target.value)}
                            placeholder="Optional notes..."
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {fileDetails.length === 0 && (
                  <div className="p-8 text-center">
                    <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                    <p className="text-gray-600">No files to upload.</p>
                    <p className="text-sm text-gray-500 mt-2">Please go back and select files.</p>
                  </div>
                )}
              </>
            )}

            {/* CSV mode - Preview */}
            {uploadMode === 'csv' && (
              <>
                {parsedData.length === 0 ? (
                  <div className="p-8 text-center">
                    <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                    <p className="text-gray-600">No data found in CSV file.</p>
                    <p className="text-sm text-gray-500 mt-2">Please go back and upload a valid CSV file.</p>
                  </div>
                ) : (
                  <>
                    <div className="max-h-80 overflow-auto border border-sand rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-sand/50 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-medium">Title</th>
                            <th className="text-left p-2 font-medium">Type</th>
                            <th className="text-left p-2 font-medium">Date</th>
                            <th className="text-left p-2 font-medium">Document</th>
                            <th className="text-center p-2 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parsedData.map((row, idx) => (
                            <tr key={idx} className="border-t border-sand/50">
                              <td className="p-2">{row.title}</td>
                              <td className="p-2 capitalize">{row.agreement_type}</td>
                              <td className="p-2">{row.signed_date}</td>
                              <td className="p-2 truncate max-w-[150px]">{row.document_filename}</td>
                              <td className="p-2 text-center">
                                {getMatchStatus(row.document_filename) ? (
                                  <CheckCircle2 className="h-5 w-5 text-green-500 inline" />
                                ) : (
                                  <XOctagon className="h-5 w-5 text-red-500 inline" />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Match Summary */}
                    <div className="flex items-center justify-between p-3 bg-sand/30 rounded-lg">
                      <span className="text-sm text-gray-600">Document matching status:</span>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="h-4 w-4" />
                          {parsedData.filter(r => getMatchStatus(r.document_filename)).length} matched
                        </span>
                        <span className="text-sm text-red-600 flex items-center gap-1">
                          <XOctagon className="h-4 w-4" />
                          {parsedData.filter(r => !getMatchStatus(r.document_filename)).length} missing
                        </span>
                      </div>
                    </div>
                    
                    {/* Warning if documents missing */}
                    {parsedData.some(r => !getMatchStatus(r.document_filename)) && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">Missing Documents</p>
                          <p className="text-xs text-amber-700 mt-1">
                            Some documents referenced in the CSV are not uploaded. Upload the missing files or go back to add them.
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Step 3: Results */}
        {step === 3 && results && (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                  <div>
                    <p className="text-2xl font-bold text-green-700">{results.success.length}</p>
                    <p className="text-sm text-green-600">Uploaded Successfully</p>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-center gap-3">
                  <XOctagon className="h-8 w-8 text-red-600" />
                  <div>
                    <p className="text-2xl font-bold text-red-700">{results.failed.length}</p>
                    <p className="text-sm text-red-600">Failed</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Failed Items */}
            {results.failed.length > 0 && (
              <div>
                <h4 className="font-medium text-red-800 mb-2">Failed Uploads:</h4>
                <div className="space-y-2 max-h-40 overflow-auto">
                  {results.failed.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-2 bg-red-50 rounded-lg text-sm">
                      <XOctagon className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium">Row {item.row}: {item.title}</span>
                        <p className="text-red-600 text-xs">{item.error}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Success Items */}
            {results.success.length > 0 && (
              <div>
                <h4 className="font-medium text-green-800 mb-2">Successfully Uploaded:</h4>
                <div className="space-y-1 max-h-40 overflow-auto">
                  {results.success.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-green-50 rounded-lg text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <span>{item.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Progress Bar during upload */}
        {uploading && (
          <div className="space-y-2">
            <Progress value={uploadProgress} className="h-2" />
            <p className="text-sm text-center text-gray-500">Uploading... {uploadProgress}%</p>
          </div>
        )}

        <DialogFooter className="mt-4">
          {step === 1 && (
            <>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              {uploadMode === 'files' ? (
                <Button 
                  onClick={() => setStep(2)}
                  disabled={documentFiles.length === 0}
                  className="bg-forest hover:bg-forest/90 text-white"
                  data-testid="batch-continue-btn"
                >
                  Continue ({documentFiles.length} file{documentFiles.length !== 1 ? 's' : ''})
                </Button>
              ) : (
                <Button 
                  onClick={() => setStep(2)}
                  disabled={!csvFile || documentFiles.length === 0 || parsedData.length === 0}
                  className="bg-forest hover:bg-forest/90 text-white"
                  data-testid="batch-preview-btn"
                >
                  Preview Data ({parsedData.length} rows)
                </Button>
              )}
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              {uploadMode === 'files' ? (
                <Button 
                  onClick={handleDirectUpload}
                  disabled={uploading || fileDetails.length === 0 || fileDetails.some(d => !d.title.trim())}
                  className="bg-forest hover:bg-forest/90 text-white"
                  data-testid="batch-upload-btn"
                >
                  {uploading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" />Upload {fileDetails.length} Agreement{fileDetails.length !== 1 ? 's' : ''}</>
                  )}
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit}
                  disabled={uploading || parsedData.length === 0 || parsedData.some(r => !getMatchStatus(r.document_filename))}
                  className="bg-forest hover:bg-forest/90 text-white"
                  data-testid="batch-submit-btn"
                >
                  {uploading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" />Upload All ({parsedData.length})</>
                  )}
                </Button>
              )}
            </>
          )}
          {step === 3 && (
            <Button 
              onClick={handleClose}
              className="bg-forest hover:bg-forest/90 text-white"
            >
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Edit Archived Agreement Dialog Component
const EditArchivedAgreementDialog = ({ open, onOpenChange, agreement, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    agreement_type: '',
    signed_date: '',
    notes: '',
    coach_name: '',
    coach_email: '',
    client_name: '',
    client_email: ''
  });

  useEffect(() => {
    if (agreement) {
      const coachParty = agreement.parties?.find(p => p.role === 'coach') || {};
      const clientParty = agreement.parties?.find(p => p.role === 'client') || {};
      
      setFormData({
        title: agreement.title || '',
        agreement_type: agreement.agreement_type || 'other',
        signed_date: agreement.signed_date?.split('T')[0] || '',
        notes: agreement.notes || '',
        coach_name: coachParty.name || '',
        coach_email: coachParty.email || '',
        client_name: clientParty.name || '',
        client_email: clientParty.email || ''
      });
    }
  }, [agreement]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/esign/archived/${agreement.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Agreement updated successfully');
      onSuccess();
    } catch (error) {
      console.error('Failed to update agreement:', error);
      toast.error(error.response?.data?.detail || 'Failed to update agreement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
            <PenLine className="h-5 w-5 text-forest" />
            Edit Archived Agreement
          </DialogTitle>
          <DialogDescription>
            Update the details of this archived agreement
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Title */}
          <div>
            <Label htmlFor="edit-title">Title *</Label>
            <Input
              id="edit-title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Agreement title"
            />
          </div>

          {/* Agreement Type */}
          <div>
            <Label htmlFor="edit-type">Agreement Type</Label>
            <Select 
              value={formData.agreement_type} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, agreement_type: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="coaching">Coaching Agreement</SelectItem>
                <SelectItem value="nda">NDA</SelectItem>
                <SelectItem value="liability">Liability Waiver</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Signed Date */}
          <div>
            <Label htmlFor="edit-date">Signed Date *</Label>
            <Input
              id="edit-date"
              type="date"
              value={formData.signed_date}
              onChange={(e) => setFormData(prev => ({ ...prev, signed_date: e.target.value }))}
            />
          </div>

          {/* Coach Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="edit-coach-name">Coach Name</Label>
              <Input
                id="edit-coach-name"
                value={formData.coach_name}
                onChange={(e) => setFormData(prev => ({ ...prev, coach_name: e.target.value }))}
                placeholder="Coach name"
              />
            </div>
            <div>
              <Label htmlFor="edit-coach-email">Coach Email</Label>
              <Input
                id="edit-coach-email"
                type="email"
                value={formData.coach_email}
                onChange={(e) => setFormData(prev => ({ ...prev, coach_email: e.target.value }))}
                placeholder="coach@example.com"
              />
            </div>
          </div>

          {/* Client Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="edit-client-name">Client Name</Label>
              <Input
                id="edit-client-name"
                value={formData.client_name}
                onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                placeholder="Client name"
              />
            </div>
            <div>
              <Label htmlFor="edit-client-email">Client Email</Label>
              <Input
                id="edit-client-email"
                type="email"
                value={formData.client_email}
                onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                placeholder="client@example.com"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="edit-notes">Notes</Label>
            <Textarea
              id="edit-notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes about this agreement..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={saving || !formData.title || !formData.signed_date}
            className="bg-forest hover:bg-forest/90 text-white"
          >
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminESignatures;
